import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faCheckCircle,
  faPhone,
  faBan,
  faUserSlash,
  faCalendarAlt,
  faExclamationTriangle,
  faTimes,
  faArrowRight,
  faExternalLink,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';

const statusOptions = [
  { value: 'ok', label: 'OK', icon: faCheckCircle, color: 'bg-green-500 hover:bg-green-600' },
  { value: 'boite_vocal', label: 'Boîte Vocale', icon: faPhone, color: 'bg-blue-500 hover:bg-blue-600' },
  { value: 'refus', label: 'Refus', icon: faBan, color: 'bg-red-500 hover:bg-red-600' },
  { value: 'hors_cible', label: 'Hors Cible', icon: faUserSlash, color: 'bg-orange-500 hover:bg-orange-600' },
  { value: 'rendez_vous', label: 'Rendez-vous', icon: faCalendarAlt, color: 'bg-purple-500 hover:bg-purple-600' },
  { value: 'injoignable', label: 'Injoignable', icon: faExclamationTriangle, color: 'bg-yellow-500 hover:bg-yellow-600' },
  { value: 'faux_numero', label: 'Faux Numéro', icon: faTimes, color: 'bg-gray-500 hover:bg-gray-600' }
];

const toDatetimeLocal = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const buildSurveyUrl = (line, limesurveyUrl, currentPost) => {
  if (!limesurveyUrl || !line?.upload?.survey?.limesurvey_id) return null;
  const surveyId = line.upload.survey.limesurvey_id;
  let baseUrl = limesurveyUrl.replace(/\/$/, '');
  baseUrl = baseUrl.replace(/\/admin\/remotecontrol.*$/i, '');
  baseUrl = baseUrl.replace(/\/index\.php\/admin\/remotecontrol.*$/i, '');
  try {
    const urlObj = new URL(baseUrl);
    baseUrl = `${urlObj.protocol}//${urlObj.host}`;
  } catch (e) {
    baseUrl = baseUrl.replace(/\/index\.php.*$/i, '');
  }
  const lineData = line.line_data || {};
  const params = new URLSearchParams();
  params.append('newtest', 'Y');
  params.append('lang', 'fr');
  const poste = currentPost?.number != null ? String(currentPost.number) : '';
  if (poste) params.append('Poste', poste);
  const tel = lineData['Num_tel'] ?? lineData['num_tel'] ?? lineData['TEL'] ?? lineData['tel'] ?? lineData['Téléphone'] ?? lineData['téléphone'] ?? '';
  const telStr = Array.isArray(tel) ? tel.join('') : (tel != null ? String(tel).trim() : '');
  if (telStr) params.append('TEL', telStr);
  const client = lineData['NOM CLIEN'] ?? lineData['NOM CLIENT'] ?? lineData['Nom client'] ?? lineData['nom client'] ?? '';
  const clientStr = Array.isArray(client) ? client.join('') : (client != null ? String(client).trim() : '');
  if (clientStr) params.append('Client', clientStr);
  const code = line.code_agence ?? '';
  if (code) params.append('Code', code);
  const segmentRaw = lineData['segment'] ?? lineData['Segment'] ?? '';
  const segmentVal = Array.isArray(segmentRaw) ? segmentRaw.join('') : (segmentRaw != null ? String(segmentRaw).trim() : '');
  const segmentMap = {
    'NOUVEAU CLIENT': '1', 'NV': '1', 'NOUVEAU CL': '1',
    'S1 - EXCEL': 'A7', 'S1': 'A7', 'S2 - PREMIUM': 'A6', 'S2 - PREMI': 'A6', 'S2': 'A6',
    'S3 - POTENTIEL': 'A9', 'S3 - POTEN': 'A9', 'S3': 'A9', 'S4 - AVENIR': 'A5', 'S4 - AVENI': 'A5', 'S4': 'A5',
    'S5 - UNIVERSEL': 'A13', 'S5 - UNIVE': 'A13', 'S5': 'A13', 'S6 - LIB': 'A18', 'S6': 'A18',
    '02': 'A1', '03': 'A10', '01': 'A8', '04': 'A4', 'GP': 'A3', 'G1': 'A2', 'CX': 'A12', 'G3': 'A11', '00': 'A14', 'G5': 'A16', 'G4': 'A15', 'G2': 'A17'
  };
  const segmentKey = Object.keys(segmentMap).find(k => k.toUpperCase() === segmentVal.toUpperCase());
  const segment = segmentKey ? segmentMap[segmentKey] : '';
  if (segment) params.append('Segments', segment);
  const marcheRaw = lineData['Marché'] ?? lineData['Marche'] ?? lineData['marché'] ?? lineData['marche'] ?? '';
  const marcheVal = Array.isArray(marcheRaw) ? marcheRaw.join('') : (marcheRaw != null ? String(marcheRaw).trim() : '');
  const cibleMap = { 'PARTICULIER': 'A1', 'PROFESSIONNELS': 'A2' };
  const cibleKey = Object.keys(cibleMap).find(k => k.toUpperCase() === marcheVal.toUpperCase());
  const cible = cibleKey ? cibleMap[cibleKey] : '';
  if (cible) params.append('Cible', cible);
  return `${baseUrl}/index.php/${surveyId}?${params.toString()}`;
};

const CatiRendezVousLine = () => {
  const { lineId } = useParams();
  const navigate = useNavigate();
  const [line, setLine] = useState(null);
  const [limesurveyUrl, setLimesurveyUrl] = useState('');
  const [currentPost, setCurrentPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    result_status: '',
    comment: '',
    reported_rendez_vous: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!lineId) return;
      try {
        setLoading(true);
        const [lineRes, dashboardRes] = await Promise.all([
          api.get(`/cati/interviewer/rendez-vous/${lineId}`),
          api.get('/cati/interviewer/dashboard').catch(() => ({ data: {} }))
        ]);
        if (lineRes.data.success) {
          setLine(lineRes.data.data);
          if (lineRes.data.limesurvey_url) setLimesurveyUrl(lineRes.data.limesurvey_url);
        } else {
          setLine(null);
        }
        if (dashboardRes?.data?.success && dashboardRes.data.data?.assignment?.currentPost) {
          setCurrentPost(dashboardRes.data.data.assignment.currentPost);
        }
      } catch (err) {
        setLine(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [lineId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleStatusChange = (status) => {
    setFormData((prev) => ({
      ...prev,
      result_status: status,
      comment: status !== 'hors_cible' ? '' : prev.comment,
      reported_rendez_vous: status === 'rendez_vous' ? toDatetimeLocal(line?.reported_rendez_vous) : ''
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.result_status) newErrors.result_status = 'Veuillez sélectionner un statut';
    if (formData.result_status === 'hors_cible' && !formData.comment?.trim()) newErrors.comment = 'Le commentaire est obligatoire pour le statut "hors cible"';
    if (formData.result_status === 'rendez_vous' && !formData.reported_rendez_vous) newErrors.reported_rendez_vous = 'La date de rendez-vous est obligatoire';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm() || !line) return;
    setSubmitting(true);
    try {
      const response = await api.put(`/cati/interviewer/lines/${line.id}/update-result`, {
        result_status: formData.result_status,
        comment: formData.comment || null,
        reported_rendez_vous: formData.reported_rendez_vous || null
      });
      if (response.data.success) {
        navigate('/cati/interviewer/rendez-vous');
      } else {
        if (response.data.errors) setErrors(response.data.errors);
      }
    } catch (err) {
      if (err.response?.data?.errors) setErrors(err.response.data.errors || {});
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="text-4xl text-red-600 animate-spin mb-4" />
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!line) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Ligne non trouvée ou accès non autorisé.</p>
          <button
            type="button"
            onClick={() => navigate('/cati/interviewer/rendez-vous')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Retour aux rendez-vous
          </button>
        </div>
      </div>
    );
  }

  const surveyUrl = buildSurveyUrl(line, limesurveyUrl, currentPost);

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
        backgroundSize: '20px 20px'
      }} />
      <div className="relative z-10 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-4">
            <button
              type="button"
              onClick={() => navigate('/cati/interviewer/rendez-vous')}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <FontAwesomeIcon icon={faArrowLeft} /> Retour aux rendez-vous
            </button>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Titre de l'Enquête</p>
                <p className="text-lg text-gray-900">{line.upload?.survey?.title || '–'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">ID LimeSurvey</p>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-lg text-gray-900">{line.upload?.survey?.limesurvey_id || '–'}</p>
                  {surveyUrl && (
                    <a
                      href={surveyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium w-full sm:w-auto flex-shrink-0"
                    >
                      <FontAwesomeIcon icon={faExternalLink} className="mr-2" />
                      Ouvrir l'enquête
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Informations Client</h2>
              <div className="space-y-3">
                {Object.entries(line.line_data || {}).map(([key, value]) => (
                  <div key={key} className="border-b border-gray-200 pb-2">
                    <p className="text-sm font-medium text-gray-500 uppercase">{key}</p>
                    <p className="text-lg text-gray-900">{value || '-'}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Statut de l'Appel</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3">Résultat *</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {statusOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleStatusChange(option.value)}
                        className={`flex items-center justify-center px-4 py-3 rounded-lg text-white transition-colors ${
                          formData.result_status === option.value ? option.color : 'bg-gray-300 hover:bg-gray-400'
                        }`}
                      >
                        <FontAwesomeIcon icon={option.icon} className="mr-2" />
                        {option.label}
                      </button>
                    ))}
                  </div>
                  {errors.result_status && <p className="mt-1 text-sm text-red-600">{errors.result_status}</p>}
                </div>

                {formData.result_status === 'hors_cible' && (
                  <div>
                    <label htmlFor="comment" className="block text-sm font-bold text-gray-700 mb-2">Commentaire *</label>
                    <textarea
                      id="comment"
                      name="comment"
                      value={formData.comment}
                      onChange={handleInputChange}
                      rows={4}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 ${errors.comment ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Expliquez pourquoi c'est hors cible..."
                    />
                    {errors.comment && <p className="mt-1 text-sm text-red-600">{errors.comment}</p>}
                  </div>
                )}

                {formData.result_status === 'rendez_vous' && (
                  <div>
                    <label htmlFor="reported_rendez_vous" className="block text-sm font-bold text-gray-700 mb-2">Date et Heure du Rendez-vous *</label>
                    <input
                      type="datetime-local"
                      id="reported_rendez_vous"
                      name="reported_rendez_vous"
                      value={formData.reported_rendez_vous}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 ${errors.reported_rendez_vous ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {errors.reported_rendez_vous && <p className="mt-1 text-sm text-red-600">{errors.reported_rendez_vous}</p>}
                  </div>
                )}

                {formData.result_status && formData.result_status !== 'hors_cible' && (
                  <div>
                    <label htmlFor="comment_opt" className="block text-sm font-bold text-gray-700 mb-2">Commentaire (optionnel)</label>
                    <textarea
                      id="comment_opt"
                      name="comment"
                      value={formData.comment}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                      placeholder="Ajoutez un commentaire si nécessaire..."
                    />
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      Enregistrer le statut
                      <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CatiRendezVousLine;
